/**
 * Team store (Zustand)
 * Manages team selection, list, and membership
 */

import { create } from 'zustand';
import { Team, TeamMember, getTeams, getTeamMembers } from '../api/teams';

interface TeamStore {
  // State
  teams: Team[];
  activeTeamId: string | null;
  teamMembers: Record<string, TeamMember[]>; // teamId -> members[]
  isLoading: boolean;
  error: string | null;

  // Actions
  setActiveTeam: (teamId: string | null) => void;
  loadTeams: () => Promise<void>;
  loadTeamMembers: (teamId: string) => Promise<void>;
  getActiveTeam: () => Team | null;
  getActiveTeamMembers: () => TeamMember[];
  clearError: () => void;
}

export const useTeamStore = create<TeamStore>((set, get) => ({
  // Initial state
  teams: [],
  activeTeamId: localStorage.getItem('activeTeamId'),
  teamMembers: {},
  isLoading: false,
  error: null,

  // Actions
  setActiveTeam: (teamId: string | null) => {
    set({ activeTeamId: teamId });
    if (teamId) {
      localStorage.setItem('activeTeamId', teamId);
    } else {
      localStorage.removeItem('activeTeamId');
    }
  },

  loadTeams: async () => {
    set({ isLoading: true, error: null });
    try {
      const teams = await getTeams();
      set({ teams });

      // Auto-select first team if none selected
      const state = get();
      if (!state.activeTeamId && teams.length > 0) {
        state.setActiveTeam(teams[0].id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load teams';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  loadTeamMembers: async (teamId: string) => {
    set({ isLoading: true, error: null });
    try {
      const members = await getTeamMembers(teamId);
      set((state) => ({
        teamMembers: { ...state.teamMembers, [teamId]: members },
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load team members';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  getActiveTeam: () => {
    const state = get();
    if (!state.activeTeamId) return null;
    return state.teams.find((t) => t.id === state.activeTeamId) || null;
  },

  getActiveTeamMembers: () => {
    const state = get();
    if (!state.activeTeamId) return [];
    return state.teamMembers[state.activeTeamId] || [];
  },

  clearError: () => set({ error: null }),
}));
